<template>
    <footer
        class="global_footer weeducStudentFooter"
        :class="{ dark }"
    >
        <div class="student-footer__inner">
            <div class="student-footer__brand">
                <img
                    :src="footerLogoSrc"
                    :alt="portalCompanyName || $t('student.footer.logoAlt')"
                />
            </div>
            <div class="student-footer__meta">
                <p class="student-footer__copyright">
                    {{ copyrightText }}
                </p>
                <nav
                    v-if="corporateFooterLinks.length"
                    class="student-footer__links"
                    :aria-label="$t('student.footer.footerLinks')"
                >
                    <a
                        v-for="(link, index) in corporateFooterLinks"
                        :key="`footer-link-${index}`"
                        :href="link.url || '#'"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {{ link.label }}
                    </a>
                </nav>
                <p class="student-footer__version">
                    {{ versionLine }}
                </p>
            </div>
        </div>
    </footer>
</template>

<script>
import { mapState } from 'vuex';
import logoImg from '@/assets/logo.png';
import logoWhiteImg from '@/assets/logo_white.png';
import { loadAppVersion } from '@/utils/appVersion';

export default {
    props: {
        view: {
            type: Boolean,
            default: false,
        },
    },
    inheritAttrs: false,
    data() {
        return {
            logoImg,
            logoWhiteImg,
            versionPortalWeeduc: '',
        };
    },
    computed: {
        ...mapState(['dark', 'corporateCompanySettings']),
        ...mapState('portal', ['dataWebsitePortal2']),
        corporateFooter() {
            return this.corporateCompanySettings?.footer || null;
        },
        corporateFooterLinks() {
            return (this.corporateFooter?.links || []).filter((l) => l?.label);
        },
        portalCompanyName() {
            if (this.corporateCompanySettings?.tradeName) {
                return String(this.corporateCompanySettings.tradeName).trim();
            }
            const w = this.dataWebsitePortal2;
            if (!w) return '';
            const raw =
                w.company?.tradeName ||
                w.company?.name ||
                w.nome ||
                w.title ||
                '';
            return String(raw).trim();
        },
        /** Logo remota do portal: no escuro usa logoFinal (tipicamente clara); no claro evita PNG branco ilegível. */
        footerLogoSrc() {
            if (this.corporateFooter && this.corporateFooter.showLogo === false) {
                return this.dark ? this.logoWhiteImg : this.logoImg;
            }
            if (this.corporateCompanySettings?.logoUrl) {
                return this.corporateCompanySettings.logoUrl;
            }
            const remote = this.dataWebsitePortal2?.logoFinal;
            if (this.dark) {
                return remote || this.logoWhiteImg;
            }
            if (
                remote &&
                !/logo_white|\/white|_white\./i.test(String(remote))
            ) {
                return remote;
            }
            return this.logoImg;
        },
        currentYear() {
            return new Date().getFullYear();
        },
        copyrightText() {
            if (this.corporateFooter?.text) {
                return this.corporateFooter.text;
            }
            if (this.portalCompanyName) {
                return this.$t('student.footer.copyrightWithCompany', {
                    year: this.currentYear,
                    company: this.portalCompanyName,
                });
            }
            return this.$t('student.footer.copyrightDefault', { year: this.currentYear });
        },
        versionLine() {
            const envVer = this.versionPortalWeeduc || '';
            if (envVer) {
                return this.$t('student.footer.versionWithNumber', { version: envVer });
            }
            const fallback = this.formattedPortalDate;
            return fallback
                ? this.$t('student.footer.versionPortalDate', { date: fallback })
                : `${this.$t('common.version')}: —`;
        },
        formattedPortalDate() {
            const raw =
                this.dataWebsitePortal2?.company?.dateUpdate ||
                this.dataWebsitePortal2?.dateInsert;
            if (!raw) return '';
            try {
                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return '';
                return d.toLocaleDateString(this.$i18n?.locale || 'pt', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                });
            } catch {
                return '';
            }
        },
    },
    created() {
        loadAppVersion().then((data) => {
            this.versionPortalWeeduc = data.student || '';
        });
    },
};
</script>

<style>
@import url('./index.css');

.student-footer__links {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1rem;
    margin: 0.35rem 0;
}

.student-footer__links a {
    color: inherit;
    opacity: 0.85;
    text-decoration: underline;
    font-size: 0.85rem;
}
</style>
